import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, CheckCircle, Shield, Clock, FileText, MessageCircle, 
  Users, BarChart3, Globe, Sparkles, ChevronDown, Send, Phone, Mail,
  Building2, Lock, Zap, Eye, Star
} from 'lucide-react';
import API from '../lib/api';

const SECTORS = [
  'Seguridad Privada', 'Hosteleria', 'Construccion', 'Limpieza',
  'Logistica', 'Comercio', 'Industria', 'Tecnologia', 'Salud', 'Otro'
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [formType, setFormType] = useState('demo');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '', sector: '', employees: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('[data-animate]').forEach(el => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSending(true);
    try {
      await API.post('/contact', { ...formData, request_type: formType });
      setSent(true);
      setFormData({ name: '', email: '', phone: '', company: '', message: '', sector: '', employees: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const isVisible = (id) => visibleSections.has(id);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ====== NAVBAR ====== */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-gradient-to-br from-[#FF6B35] to-[#FF8B5E] rounded-xl flex items-center justify-center shadow-lg shadow-orange-200">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight text-gray-900">Urcal<span className="text-[#FF6B35]">Portal</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <a href="#features" className="hover:text-[#FF6B35] transition-colors">Funcionalidades</a>
            <a href="#sectors" className="hover:text-[#FF6B35] transition-colors">Sectores</a>
            <a href="#benefits" className="hover:text-[#FF6B35] transition-colors">Ventajas</a>
            <a href="#contact" className="hover:text-[#FF6B35] transition-colors">Contacto</a>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="px-5 py-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white text-sm font-semibold rounded-xl shadow-lg shadow-orange-200 hover:shadow-orange-300 transition-all hover:-translate-y-0.5"
            data-testid="landing-login-btn"
          >
            Acceder
          </button>
        </div>
      </nav>

      {/* ====== HERO ====== */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-40 -z-10" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-50 rounded-full blur-3xl opacity-60 -z-10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-100 rounded-full text-xs font-semibold text-[#FF6B35] mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Plataforma con Inteligencia Artificial
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight">
              Gestiona tu equipo<br />
              <span className="bg-gradient-to-r from-[#FF6B35] to-[#FF8B5E] bg-clip-text text-transparent">sin complicaciones</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-500 leading-relaxed max-w-2xl">
              La plataforma todo-en-uno para la gestion de empleados. Control horario, documentacion, comunicaciones y mucho mas. Para cualquier sector.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-[#FF6B35] hover:bg-[#E55A2B] text-white font-semibold rounded-xl shadow-lg shadow-orange-200 hover:shadow-orange-300 transition-all hover:-translate-y-0.5 text-base"
              >
                Solicitar Demo Gratuita
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a
                href="#features"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-white border-2 border-gray-200 hover:border-[#FF6B35] text-gray-700 hover:text-[#FF6B35] font-semibold rounded-xl transition-all text-base"
              >
                Ver Funcionalidades
              </a>
            </div>
            {/* Trust badges */}
            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-1.5">
                <Lock className="w-4 h-4" />
                <span>Datos encriptados</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4" />
                <span>Cumple RGPD</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Zap className="w-4 h-4" />
                <span>Configuracion en 5 min</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== FEATURES ====== */}
      <section id="features" className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="feat-header" data-animate className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${isVisible('feat-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Todo lo que necesitas</h2>
            <p className="mt-4 text-lg text-gray-500">Una sola plataforma para gestionar a todo tu equipo</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                id={`feat-${i}`}
                data-animate
                className={`group bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#FF6B35]/30 hover:shadow-xl hover:shadow-orange-100/50 transition-all duration-500 hover:-translate-y-1 ${isVisible(`feat-${i}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${f.bg} transition-transform group-hover:scale-110`}>
                  <f.icon className={`w-6 h-6 ${f.color}`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== AI HIGHLIGHT ====== */}
      <section className="py-20 bg-gradient-to-br from-[#1F2937] to-[#111827] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border border-orange-500 rounded-full" />
          <div className="absolute bottom-20 right-20 w-64 h-64 border border-orange-500/50 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-20 h-20 border border-orange-500/30 rounded-full" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div id="ai-section" data-animate className={`max-w-3xl mx-auto text-center transition-all duration-700 ${isVisible('ai-section') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-full text-xs font-semibold text-[#FF8B5E] mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Potenciado con IA
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Crea trabajadores en segundos<br />con Inteligencia Artificial
            </h2>
            <p className="mt-5 text-lg text-gray-400 max-w-xl mx-auto">
              Sube una foto del DNI, nomina o contrato y nuestra IA extrae automaticamente todos los datos. Importacion masiva de multiples documentos a la vez.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg mx-auto">
              {[
                { num: '1', text: 'Sube el documento' },
                { num: '2', text: 'La IA extrae los datos' },
                { num: '3', text: 'Trabajador creado' }
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-[#FF6B35] flex items-center justify-center text-white font-bold text-sm">{step.num}</div>
                  <span className="text-sm text-gray-300">{step.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTORS ====== */}
      <section id="sectors" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="sectors-header" data-animate className={`text-center max-w-2xl mx-auto mb-14 transition-all duration-700 ${isVisible('sectors-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Para todos los sectores</h2>
            <p className="mt-4 text-lg text-gray-500">Adaptable a las necesidades de cualquier industria</p>
          </div>
          <div id="sectors-grid" data-animate className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 transition-all duration-700 ${isVisible('sectors-grid') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {SECTORS.map((sector, i) => (
              <div key={i} className="text-center py-5 px-3 bg-white border border-gray-100 rounded-2xl hover:border-[#FF6B35]/30 hover:shadow-lg hover:shadow-orange-100/30 transition-all hover:-translate-y-1 cursor-default">
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <span className="text-sm font-medium text-gray-700">{sector}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== BENEFITS ====== */}
      <section id="benefits" className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="benefits-header" data-animate className={`text-center max-w-2xl mx-auto mb-14 transition-all duration-700 ${isVisible('benefits-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Por que elegir UrcalPortal</h2>
            <p className="mt-4 text-lg text-gray-500">Ventajas que marcan la diferencia</p>
          </div>
          <div id="benefits-grid" data-animate className={`grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto transition-all duration-700 ${isVisible('benefits-grid') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white rounded-2xl border border-gray-100">
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{b.title}</h3>
                  <p className="text-sm text-gray-500">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== STATS ====== */}
      <section className="py-16 bg-[#FF6B35]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '20+', label: 'Funcionalidades' },
              { value: '99.9%', label: 'Disponibilidad' },
              { value: '5 min', label: 'Configuracion' },
              { value: '24/7', label: 'Soporte' }
            ].map((s, i) => (
              <div key={i}>
                <p className="text-3xl sm:text-4xl font-bold text-white">{s.value}</p>
                <p className="text-sm text-orange-100 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CONTACT / DEMO FORM ====== */}
      <section id="contact" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Info */}
            <div id="contact-info" data-animate className={`transition-all duration-700 ${isVisible('contact-info') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                Empieza hoy mismo
              </h2>
              <p className="mt-4 text-lg text-gray-500 leading-relaxed">
                Solicita una demo personalizada o contacta con nuestro equipo. Te responderemos en menos de 24 horas.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <span className="text-sm font-medium">Demo personalizada para tu sector</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <span className="text-sm font-medium">Sin compromiso ni permanencia</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <span className="text-sm font-medium">Configuracion asistida incluida</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <span className="text-sm font-medium">Soporte tecnico 24/7</span>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div id="contact-form" data-animate className={`transition-all duration-700 ${isVisible('contact-form') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {sent ? (
                <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-xl shadow-gray-100">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Solicitud enviada</h3>
                  <p className="text-gray-500 mb-6">Nos pondremos en contacto contigo lo antes posible.</p>
                  <button onClick={() => setSent(false)} className="text-[#FF6B35] font-medium hover:underline text-sm">
                    Enviar otra solicitud
                  </button>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-xl shadow-gray-100">
                  {/* Form type toggle */}
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6">
                    <button
                      onClick={() => setFormType('demo')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${formType === 'demo' ? 'bg-[#FF6B35] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      Solicitar Demo
                    </button>
                    <button
                      onClick={() => setFormType('contact')}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${formType === 'contact' ? 'bg-[#FF6B35] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      Contactar
                    </button>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nombre *</label>
                        <input
                          type="text" required value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all"
                          placeholder="Tu nombre"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email *</label>
                        <input
                          type="email" required value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all"
                          placeholder="tu@email.com"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefono</label>
                        <input
                          type="tel" value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all"
                          placeholder="+34 600 000 000"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Empresa</label>
                        <input
                          type="text" value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all"
                          placeholder="Nombre de tu empresa"
                        />
                      </div>
                    </div>
                    {formType === 'demo' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sector</label>
                          <select
                            value={formData.sector}
                            onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all bg-white"
                          >
                            <option value="">Selecciona sector</option>
                            {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">N. Empleados</label>
                          <select
                            value={formData.employees}
                            onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all bg-white"
                          >
                            <option value="">Selecciona</option>
                            <option value="1-10">1 - 10</option>
                            <option value="11-50">11 - 50</option>
                            <option value="51-200">51 - 200</option>
                            <option value="200+">Mas de 200</option>
                          </select>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        {formType === 'demo' ? 'Cuentanos sobre tu empresa *' : 'Mensaje *'}
                      </label>
                      <textarea
                        required value={formData.message} rows={3}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] outline-none transition-all resize-none"
                        placeholder={formType === 'demo' ? 'Que necesidades tiene tu empresa...' : 'Tu mensaje...'}
                        data-testid="contact-message"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full py-3 bg-[#FF6B35] hover:bg-[#E55A2B] text-white font-semibold rounded-xl shadow-lg shadow-orange-200 hover:shadow-orange-300 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {sending ? 'Enviando...' : (
                        <>
                          <Send className="w-4 h-4" />
                          {formType === 'demo' ? 'Solicitar Demo Gratuita' : 'Enviar Mensaje'}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <footer className="bg-[#1F2937] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-[#FF6B35] to-[#FF8B5E] rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="text-base font-bold text-white">Urcal<span className="text-[#FF6B35]">Portal</span></span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="#features" className="hover:text-white transition-colors">Funcionalidades</a>
              <a href="#sectors" className="hover:text-white transition-colors">Sectores</a>
              <a href="#contact" className="hover:text-white transition-colors">Contacto</a>
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Acceder</button>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-700 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} UrcalPortal. Todos los derechos reservados. Portal de gestion de empleados.
          </div>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  { icon: Clock, title: 'Control Horario GPS', desc: 'Fichaje geolocalizado con firma digital. Exportacion PDF/Excel conforme al Estatuto de los Trabajadores.', bg: 'bg-orange-50', color: 'text-[#FF6B35]' },
  { icon: FileText, title: 'Gestion Documental', desc: 'Sube, valida y controla la caducidad de todos los documentos de tus trabajadores desde un solo lugar.', bg: 'bg-blue-50', color: 'text-blue-600' },
  { icon: MessageCircle, title: 'Comunicaciones', desc: 'Envia comunicados, adjuntos y recibe respuestas. Chat interno en tiempo real entre empresa y empleados.', bg: 'bg-emerald-50', color: 'text-emerald-600' },
  { icon: Sparkles, title: 'Creacion con IA', desc: 'Sube un DNI o contrato y la Inteligencia Artificial extrae automaticamente todos los datos del trabajador.', bg: 'bg-purple-50', color: 'text-purple-600' },
  { icon: Users, title: 'Perfiles Completos', desc: 'Ficha detallada de cada trabajador: DNI, Seguridad Social, IBAN, puesto, centro de trabajo y mucho mas.', bg: 'bg-amber-50', color: 'text-amber-600' },
  { icon: BarChart3, title: 'Estadisticas y Reportes', desc: 'Dashboard analitico con graficos en tiempo real. Informes diarios descargables en PDF.', bg: 'bg-cyan-50', color: 'text-cyan-600' },
  { icon: Shield, title: 'Canal de Denuncias', desc: 'Canal anonimo para que los trabajadores reporten situaciones irregulares. Cumplimiento normativo.', bg: 'bg-red-50', color: 'text-red-600' },
  { icon: Globe, title: 'Portal Publico', desc: 'Landing page personalizable para cada empresa con logo, colores corporativos y 3 plantillas de diseno.', bg: 'bg-indigo-50', color: 'text-indigo-600' },
  { icon: Eye, title: 'Documentos Firmables', desc: 'Envia documentos para firmar digitalmente. Controla quien ha firmado y cuando desde tu panel.', bg: 'bg-pink-50', color: 'text-pink-600' },
];

const BENEFITS = [
  { title: 'Ahorra tiempo', desc: 'Automatiza la gestion de documentos, fichajes y comunicaciones. Hasta un 80% menos de tareas administrativas.' },
  { title: 'Cumple la normativa', desc: 'Control horario conforme al Art. 34.9 del Estatuto de los Trabajadores. Canal de denuncias segun Ley 2/2023.' },
  { title: 'Seguridad total', desc: 'Datos encriptados, autenticacion segura, almacenamiento en la nube con copias de seguridad automaticas.' },
  { title: 'Facil de usar', desc: 'Interfaz intuitiva que no requiere formacion. Tus empleados lo usaran desde el primer dia. Compatible con movil.' },
  { title: 'Todo centralizado', desc: 'Documentos, fichajes, comunicaciones, turnos y denuncias en una sola plataforma. Sin cambiar de herramienta.' },
  { title: 'Soporte dedicado', desc: 'Equipo de soporte disponible para ayudarte en la configuracion y resolucion de cualquier duda.' },
];
